(() => {
  'use strict';

  const storageKey = 'unsub:recently-viewed:v1';
  const eventName = 'unsub:recently-viewed:changed';
  const historyLimit = 50;
  const cardCache = new Map();
  // Product handles in this catalog include underscores (for example 1208_4514_900).
  const validHandle = (handle) => typeof handle === 'string' && handle.length <= 255 && /^[a-z0-9][a-z0-9_-]*$/.test(handle);
  let memory = [];
  let storageAvailable = true;

  function read() {
    if (!storageAvailable) return memory.slice();
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
      memory = Array.isArray(saved) ? [...new Set(saved.filter(validHandle))].slice(0, historyLimit) : [];
    } catch (error) {
      if (error instanceof SyntaxError) memory = [];
      else storageAvailable = false;
      // Private browsing and disabled storage still support this page's views.
    }
    return memory.slice();
  }

  function record(handle) {
    if (!validHandle(handle)) return;
    memory = [handle, ...read().filter((item) => item !== handle)].slice(0, historyLimit);
    try {
      if (storageAvailable) localStorage.setItem(storageKey, JSON.stringify(memory));
    } catch (_) { storageAvailable = false; }
    document.dispatchEvent(new CustomEvent(eventName));
  }

  window.UnsubRecentlyViewed = { read, record };
  const currentHandle = document.currentScript?.dataset.productHandle;
  if (currentHandle) record(currentHandle);

  function waitForStyles() {
    const stylesheet = document.querySelector('link[href*="/styles.css"]');
    if (!stylesheet || (stylesheet.rel === 'stylesheet' && stylesheet.sheet)) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => {
        stylesheet.removeEventListener('load', done);
        stylesheet.removeEventListener('error', done);
        resolve();
      };
      stylesheet.addEventListener('load', done);
      stylesheet.addEventListener('error', done);
    });
  }

  function fetchCard(handle) {
    if (!cardCache.has(handle)) {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}products/${encodeURIComponent(handle)}?view=recently-viewed-card`;
      cardCache.set(handle, fetch(url, { signal: AbortSignal.timeout(10000) })
        .then((response) => response.ok ? response.text() : '')
        .then((html) => new DOMParser().parseFromString(html, 'text/html'))
        .catch(() => {
          cardCache.delete(handle);
          return null;
        }));
    }
    return cardCache.get(handle);
  }

  class RecentlyViewedCollection extends HTMLElement {
    connectedCallback() {
      this.container = this.querySelector('[data-recent-products]');
      this.heading = this.querySelector('[data-recent-heading]');
      this.titleElement = this.querySelector('[data-recent-title]');
      this.viewAll = this.querySelector('[data-recent-view-all]');
      this.fallback ??= this.container.innerHTML;
      this.fallbackTitle ??= this.titleElement.innerHTML;
      this.refresh = this.render.bind(this);
      this.version ??= 0;
      document.addEventListener(eventName, this.refresh);
      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener(eventName, this.refresh);
      this.version += 1;
      this.querySelector('.swiper-container')?.swiper?.destroy(true, true);
    }

    async render() {
      const version = ++this.version;
      await waitForStyles();
      if (version !== this.version || !this.isConnected) return;
      // Size the server-rendered fallback while hidden, preserving its space
      // during product requests without showing uninitialized slides.
      const initialSlider = this.querySelector('.swiper-container');
      if (initialSlider && !initialSlider.swiper && window.theme?.initProductSlider && window.jQuery) {
        window.theme.initProductSlider(window.jQuery(initialSlider));
      }
      const handles = read();
      const limit = Number(this.dataset.limit);
      const minimum = Number(this.dataset.minimum);
      const cards = [];
      // Limit concurrent requests; skip deleted products and try older history.
      if (handles.length >= minimum) {
        for (let offset = 0; offset < handles.length && cards.length < Math.max(limit, minimum); offset += 4) {
          const documents = await Promise.all(handles.slice(offset, offset + 4).map(fetchCard));
          if (version !== this.version || !this.isConnected) return;
          documents.forEach((doc) => {
            const kind = this.dataset.hideSwatches === 'true' ? 'plain' : 'swatches';
            const card = doc?.querySelector(`[data-recent-card="${kind}"]`)?.content.firstElementChild;
            if (card) cards.push(card.cloneNode(true));
          });
        }
      }
      if (version !== this.version || !this.isConnected) return;
      const showRecent = cards.length >= minimum;
      this.style.visibility = 'hidden';
      this.querySelector('.swiper-container')?.swiper?.destroy(true, true);
      window.jQuery?.(document).trigger('cc:component:unload', ['product-block', this]);
      this.style.display = showRecent || this.dataset.hasFallback === 'true' ? 'block' : 'none';

      if (showRecent) {
        const fragment = document.createDocumentFragment();
        cards.slice(0, limit).forEach((card) => {
          if (this.dataset.layout === 'carousel') {
            const slide = document.createElement('div');
            slide.className = 'swiper-slide';
            slide.appendChild(card);
            fragment.appendChild(slide);
          } else {
            if (this.dataset.layout === 'rows') {
              card.classList.add('column', { 2: 'half', 3: 'third', 4: 'quarter' }[this.dataset.grid]);
            }
            fragment.appendChild(card);
          }
        });
        this.container.replaceChildren(fragment);
        this.titleElement.textContent = this.dataset.title;
      } else {
        this.container.innerHTML = this.fallback;
        this.titleElement.innerHTML = this.fallbackTitle;
      }
      if (this.viewAll) this.viewAll.hidden = showRecent;
      this.titleElement.hidden = !this.titleElement.textContent.trim();
      this.heading.hidden = this.titleElement.hidden && (!this.viewAll || this.viewAll.hidden);
      if (this.style.display === 'none') return;

      const slider = this.querySelector('.swiper-container');
      if (slider && window.theme?.initProductSlider && window.jQuery) {
        window.theme.initProductSlider(window.jQuery(slider));
      }
      window.jQuery?.(document).trigger('cc:component:load', ['product-block', this]);
      window.theme?.inlineVideos?.init(this);
      window.theme?.initAnimateOnScroll?.();
      // Reveal only after Swiper and the product components have applied their
      // dimensions. A newer render or a removed section must not be revealed.
      requestAnimationFrame(() => {
        if (version !== this.version || !this.isConnected) return;
        slider?.swiper?.update();
        this.style.visibility = 'visible';
      });
    }
  }

  customElements.define('recently-viewed-collection', RecentlyViewedCollection);
  window.addEventListener('storage', (event) => {
    if (event.key === storageKey || event.key === null) {
      document.dispatchEvent(new CustomEvent(eventName));
    }
  });
})();
